import type { OfflineStorage } from '@scrambl/shared/offline'
import {
  OfflineLedgerStorageError,
  OfflineWriteIntentConflictError,
  parseOwnedOfflineSnapshot,
} from '@scrambl/shared/offline'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import type {
  OwnedSnapshot,
  SnapshotRecoveryCopy,
} from './owned-snapshot-store'
import {
  createOwnedSnapshotStore,
  SnapshotConflictError,
  SnapshotOwnerRemovedError,
} from './owned-snapshot-store'

export class OfflineLedgerNotReadyError extends OfflineWriteIntentConflictError {
  constructor() {
    super()
    this.message =
      'Saved changes are still loading. Keep your draft and try again.'
    this.name = 'OfflineLedgerNotReadyError'
  }
}

/** Storage conflicts stop automatic transport retry; their draft remains local. */
export class OfflineLedgerSnapshotConflictError extends OfflineWriteIntentConflictError {
  constructor(message: string) {
    super()
    this.name = 'OfflineLedgerSnapshotConflictError'
    this.message = message
  }
}
function throwLedgerError(error: unknown): never {
  if (
    error instanceof SnapshotConflictError ||
    error instanceof SnapshotOwnerRemovedError
  )
    throw new OfflineLedgerSnapshotConflictError(error.message)
  throw error
}

/** Apply only this window's changes. A competing change to the same operation,
 * including an identical dispatch marker, requires recovery instead of delivery. */
export function mergeLedgerSnapshots(
  owner: string,
  base: string | null,
  candidate: string | null,
  current: string | null,
): string | null {
  const index = (raw: string | null) =>
    new Map(
      parseOwnedOfflineSnapshot(raw, owner).map(entry => [
        entry.envelope.idempotencyKey,
        entry,
      ]),
    )
  const before = index(base),
    after = index(candidate),
    latest = index(current)
  for (const id of new Set([...before.keys(), ...after.keys()])) {
    const previous = before.get(id),
      next = after.get(id)
    if (JSON.stringify(previous) === JSON.stringify(next)) continue
    if (JSON.stringify(latest.get(id)) !== JSON.stringify(previous))
      throw new SnapshotConflictError()
    if (next) latest.set(id, next)
    else latest.delete(id)
  }
  return latest.size ? JSON.stringify([...latest.values()]) : null
}

export interface LedgerRecoveryStatus {
  readonly state: 'saving' | 'saved' | 'failed'
  readonly message: string
}
export interface LedgerSnapshotStorage extends OfflineStorage {
  subscribe(listener: () => void): () => void
  recoveryOwners(): string[]
  recoveryStatus(owner: string | null): LedgerRecoveryStatus | null
  preserveForReload(owner: string, candidate: string | null): Promise<void>
  readRecoveries(owner: string): Promise<SnapshotRecoveryCopy[]>
  removeRecovery(copy: SnapshotRecoveryCopy): Promise<void>
  /** Caller must hold a durable native removal receipt with retired credentials. */
  forgetOwner(owner: string, operationId: string): Promise<void>
}

type PendingWrite = {
  expected: OwnedSnapshot
  value: string | null
  operation: string
  candidate: string | null
}
type Binding = {
  snapshot: OwnedSnapshot
  local: string | null
  pending?: PendingWrite
  conflict?: OfflineLedgerSnapshotConflictError
  recoveries: Map<string, SnapshotRecoveryCopy>
  recoveryAttempt?: symbol
  assertOwner?: () => void
}

/** IndexedDB is authoritative after first import. Keep the legacy recovery copy:
 * localStorage cannot atomically compare-and-remove across windows. Older builds
 * must reload; only explicit account cleanup removes their original bytes. */
export function createLedgerSnapshotStorage(options: {
  legacyKey: string
  legacy: OfflineStorage
  snapshots?: ReturnType<typeof createOwnedSnapshotStore>
  captureOwner?(
    owner: string,
  ): ((() => Promise<void>) & { assertCurrent?: () => void }) | undefined
  isDeleted?(owner: string): boolean
  onRetireOwner?(owner: string): void
}): LedgerSnapshotStorage {
  const snapshots = options.snapshots ?? createOwnedSnapshotStore()
  const bindings = new Map<string, Binding>()
  const statuses = new Map<string, LedgerRecoveryStatus>()
  const listeners = new Set<() => void>()
  const notify = () => {
    for (const listener of listeners) {
      try {
        listener()
      } catch {
        /* A view cannot invalidate durable storage. */
      }
    }
  }
  const status = (owner: string, state: LedgerRecoveryStatus['state']) => {
    statuses.set(
      owner,
      Object.freeze({
        state,
        message:
          state === 'saved'
            ? 'A local recovery copy is saved. Reload the app to use the latest saved changes.'
            : state === 'saving'
              ? 'Saving a local recovery copy. Keep this window open.'
              : 'The recovery copy could not be saved. Keep this window open and retry before reloading.',
      }),
    )
    notify()
  }
  const opening = new Map<string, Promise<Binding>>()
  const prefix = `${options.legacyKey}:v2:`
  const ownerFor = (key: string) => {
    if (!key.startsWith(prefix)) return null
    const owner = decodeURIComponent(key.slice(prefix.length))
    if (!owner || key !== `${prefix}${encodeURIComponent(owner)}`)
      throw new OfflineLedgerStorageError()
    return owner
  }
  const assertLive = (owner: string) => {
    if (options.isDeleted?.(owner)) throw new SnapshotOwnerRemovedError()
  }
  const assertBinding = (owner: string, binding: Binding) => {
    assertLive(owner)
    if (bindings.get(owner) !== binding) throw new SnapshotOwnerRemovedError()
  }
  const requireBinding = (owner: string) => {
    assertLive(owner)
    const binding = bindings.get(owner)
    if (!binding) throw new OfflineLedgerNotReadyError()
    return binding
  }
  const assertGeneration = (binding: Binding, snapshot: OwnedSnapshot) => {
    if (snapshot.generation !== binding.snapshot.generation)
      throw new SnapshotOwnerRemovedError()
  }
  const retireBinding = (owner: string, binding: Binding) => {
    if (bindings.get(owner) !== binding) return
    bindings.delete(owner)
    statuses.delete(owner)
    options.onRetireOwner?.(owner)
    notify()
  }
  const initialize = (owner: string, key: string): Promise<Binding> => {
    const guard = options.captureOwner?.(owner)
    const known = opening.get(owner)
    if (known)
      return known.then(
        async binding => {
          await guard?.()
          assertBinding(owner, binding)
          binding.assertOwner = guard?.assertCurrent
          return binding
        },
        async error => {
          if (
            !guard?.assertCurrent ||
            !(
              error instanceof SessionChangedError ||
              error instanceof SnapshotOwnerRemovedError
            )
          )
            throw error
          await guard()
          if (opening.get(owner) === known) opening.delete(owner)
          return initialize(owner, key)
        },
      )
    const pending = (async () => {
      assertLive(owner)
      let snapshot = await snapshots.read(owner)
      await guard?.()
      assertLive(owner)
      parseOwnedOfflineSnapshot(snapshot.value, owner)
      const existing = bindings.get(owner)
      if (existing) {
        if (existing.snapshot.generation === snapshot.generation) {
          existing.assertOwner = guard?.assertCurrent
          return existing
        }
        if (!guard?.assertCurrent || !options.onRetireOwner)
          throw new SnapshotOwnerRemovedError()
        // A fresh, verified owner may restore after another window finished
        // cleanup. Retire the old in-memory commands before publishing new ones.
        retireBinding(owner, existing)
      }
      if (snapshot.revision === null) {
        const legacy = await options.legacy.getItem(key)
        parseOwnedOfflineSnapshot(legacy, owner)
        await guard?.()
        assertLive(owner)
        try {
          snapshot = await snapshots.write(
            snapshot,
            legacy,
            crypto.randomUUID(),
          )
        } catch (error) {
          if (!(error instanceof SnapshotConflictError)) throw error
          const replacement = await snapshots.read(owner)
          if (replacement.generation !== snapshot.generation)
            throw new SnapshotOwnerRemovedError()
          snapshot = replacement
        }
        // A competing import must never make corrupt persisted rows publishable.
        parseOwnedOfflineSnapshot(snapshot.value, owner)
      }
      parseOwnedOfflineSnapshot(snapshot.value, owner)
      const latest = await snapshots.read(owner)
      if (latest.generation !== snapshot.generation)
        throw new SnapshotOwnerRemovedError()
      await guard?.()
      assertLive(owner)
      const binding: Binding = {
        snapshot,
        local: snapshot.value,
        recoveries: new Map(),
        assertOwner: guard?.assertCurrent,
      }
      bindings.set(owner, binding)
      return binding
    })()
    opening.set(owner, pending)
    void pending
      .finally(() => {
        if (opening.get(owner) === pending) opening.delete(owner)
      })
      .catch(() => undefined)
    return pending
  }
  const finish = async (
    owner: string,
    binding: Binding,
    pending: PendingWrite,
  ) => {
    assertBinding(owner, binding)
    const saved = await snapshots.write(
      pending.expected,
      pending.value,
      pending.operation,
    )
    assertBinding(owner, binding)
    binding.snapshot = saved
    binding.local = pending.candidate
    binding.pending = undefined
  }
  const write = async (
    owner: string,
    candidate: string | null,
    binding: Binding,
  ) => {
    assertBinding(owner, binding)
    if (binding.conflict) throw binding.conflict
    parseOwnedOfflineSnapshot(candidate, owner)
    // An uncertain acknowledgement must be settled with the exact original
    // write identity before accepting another local state, never rebased blindly.
    if (binding.pending) await finish(owner, binding, binding.pending)
    for (let attempt = 0; attempt < 8; attempt++) {
      const current = await snapshots.read(owner)
      assertBinding(owner, binding)
      assertGeneration(binding, current)
      const value = mergeLedgerSnapshots(
        owner,
        binding.local,
        candidate,
        current.value,
      )
      if (candidate === binding.local) return
      const pending = {
        expected: current,
        value,
        operation: crypto.randomUUID(),
        candidate,
      }
      binding.pending = pending
      try {
        await finish(owner, binding, pending)
        return
      } catch (error) {
        // This failed comparison made no change. Re-read and merge independent
        // commands only; changes to a shared command still fail the comparison.
        if (!(error instanceof SnapshotConflictError)) throw error
        binding.pending = undefined
      }
    }
    throw new SnapshotConflictError()
  }
  const preserve = async (
    owner: string,
    candidate: string | null,
    binding: Binding,
  ) => {
    assertBinding(owner, binding)
    parseOwnedOfflineSnapshot(candidate, owner)
    parseOwnedOfflineSnapshot(binding.local, owner)
    const key = JSON.stringify([binding.local, candidate])
    let copy = binding.recoveries.get(key)
    if (!copy) {
      copy = Object.freeze({
        version: 1 as const,
        ownerId: owner,
        generation: binding.snapshot.generation,
        operationId: crypto.randomUUID(),
        createdAt: Date.now(),
        base: binding.local,
        candidate,
      })
      binding.recoveries.set(key, copy)
    }
    const attempt = Symbol()
    binding.recoveryAttempt = attempt
    status(owner, 'saving')
    try {
      await snapshots.retainRecovery(binding.snapshot, copy)
      assertBinding(owner, binding)
      if (binding.recoveryAttempt === attempt) status(owner, 'saved')
    } catch (error) {
      assertBinding(owner, binding)
      if (binding.recoveryAttempt === attempt) status(owner, 'failed')
      throw error
    }
  }
  const save = async (owner: string, candidate: string | null) => {
    const binding = bindings.get(owner)
    const recoveryAttempt = binding?.recoveryAttempt
    try {
      assertLive(owner)
      if (!binding) throw new OfflineLedgerNotReadyError()
      await write(owner, candidate, binding)
      assertBinding(owner, binding)
      if (binding.recoveryAttempt === recoveryAttempt && statuses.delete(owner))
        notify()
    } catch (error) {
      if (!binding) throwLedgerError(error)
      try {
        assertBinding(owner, binding)
      } catch (removed) {
        throwLedgerError(removed)
      }
      if (error instanceof SnapshotConflictError) {
        binding.conflict = new OfflineLedgerSnapshotConflictError(error.message)
      }
      if (!(error instanceof SnapshotOwnerRemovedError)) {
        try {
          await preserve(owner, candidate, binding)
        } catch {
          /* The failed status keeps reload disabled until an explicit retry succeeds. */
        }
      }
      throwLedgerError(error)
    }
  }
  return {
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    recoveryStatus(owner) {
      return owner ? (statuses.get(owner) ?? null) : null
    },
    recoveryOwners: () => [...statuses.keys()],
    async forgetOwner(owner, operationId) {
      // The store's durable operation receipt makes repeated/late cleanup a no-op.
      // Compare against the currently published binding after awaiting storage,
      // so a fresh binding at the new generation is never retired by an old retry.
      const binding = bindings.get(owner)
      await snapshots.forgetOwner(owner, operationId)
      const current = await snapshots.read(owner)
      if (
        binding &&
        bindings.get(owner) === binding &&
        binding.snapshot.generation !== current.generation
      )
        retireBinding(owner, binding)
      // Updated clients never write this owner key after snapshot migration.
      // The unattributed legacy quarantine is deliberately retained.
      await options.legacy.removeItem(`${prefix}${encodeURIComponent(owner)}`)
    },
    async preserveForReload(owner, candidate) {
      const binding = requireBinding(owner)
      try {
        await preserve(owner, candidate, binding)
      } catch (error) {
        // This explicit user action may create a new copy after a previous copy
        // was removed. Automatic persistence retries must keep the old identity.
        if (!(error instanceof SnapshotConflictError)) throw error
        assertBinding(owner, binding)
        binding.recoveries.delete(JSON.stringify([binding.local, candidate]))
        await preserve(owner, candidate, binding)
      }
    },
    async readRecoveries(owner) {
      const guard = options.captureOwner?.(owner)
      assertLive(owner)
      await guard?.()
      const copies = await snapshots.readRecoveries(owner)
      await guard?.()
      assertLive(owner)
      for (const copy of copies) {
        parseOwnedOfflineSnapshot(copy.base, owner)
        parseOwnedOfflineSnapshot(copy.candidate, owner)
      }
      return copies
    },
    async removeRecovery(copy) {
      const guard = options.captureOwner?.(copy.ownerId)
      assertLive(copy.ownerId)
      await guard?.()
      await snapshots.removeRecovery(copy)
      await guard?.()
      assertLive(copy.ownerId)
      notify()
    },
    assertReady(key) {
      const owner = ownerFor(key)
      if (owner) {
        assertLive(owner)
        const binding = bindings.get(owner)
        if (!binding) throw new OfflineLedgerNotReadyError()
        binding.assertOwner?.()
        if (binding.conflict) throw binding.conflict
      }
    },
    async getItem(key) {
      const owner = ownerFor(key)
      return owner
        ? (await initialize(owner, key)).local
        : options.legacy.getItem(key)
    },
    async setItem(key, value) {
      const owner = ownerFor(key)
      if (owner) await save(owner, value)
      else await options.legacy.setItem(key, value)
    },
    async removeItem(key) {
      const owner = ownerFor(key)
      if (owner && options.isDeleted?.(owner)) {
        // Permanent cleanup is privileged and can finish after credentials retire.
        // Retire this window before awaiting storage: late save/archive replies
        // must not publish status or reuse a future binding, even if cleanup fails.
        bindings.delete(owner)
        statuses.delete(owner)
        notify()
        await snapshots.deleteOwner(owner)
        await options.legacy.removeItem(key)
      } else if (owner) await save(owner, null)
      else await options.legacy.removeItem(key)
    },
  }
}
